import {
  activateDrawer,
  lockScroll,
  unlockScroll,
  bindDrawerEscape,
  afterDrawerClose,
} from '@theme/coco-utilities';

class SearchDrawer extends HTMLElement {
  constructor() {
    super();
    this.activeElement = null;
    this.releaseModal = null;
    this.unbindEscape = null;
  }

  connectedCallback() {
    this.onClick = this.onClick.bind(this);

    this.unbindEscape = bindDrawerEscape(this, () => this.close());
    this.addEventListener('click', this.onClick);
    this.bindSearchLink();
  }

  disconnectedCallback() {
    this.unbindEscape?.();
    this.unbindEscape = null;
    this.removeEventListener('click', this.onClick);
  }

  onClick(event) {
    if (!this.hasAttribute('active')) return;

    const inner = this.querySelector('[data-drawer-inner]');
    const isInsidePanel = inner?.contains(event.target);

    if (event.target.closest('[is="close-drawer"]')) {
      event.preventDefault();
      this.close();
      return;
    }

    if (!isInsidePanel) {
      event.preventDefault();
      this.close();
    }
  }

  bindSearchLink() {
    const searchLinks = document.querySelectorAll('[data-search-link]');

    searchLinks.forEach((searchLink) => {
      if (searchLink.dataset.searchDrawerBound) return;

      searchLink.dataset.searchDrawerBound = 'true';
      searchLink.setAttribute('aria-controls', 'SearchDrawer');
      searchLink.setAttribute('aria-haspopup', 'dialog');
      searchLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(searchLink);
      });
    });
  }

  getSearchInput() {
    return (
      this.querySelector('input[type="search"], input[name="q"], .search-input') ||
      null
    );
  }

  /** Suppress visible focus ring until the user types or leaves the field. */
  bindQuietFocus(input) {
    if (!input) return;

    input.classList.add('focus-none');

    const clearFocusStyle = () => {
      input.classList.remove('focus-none');
      input.removeEventListener('keydown', clearFocusStyle);
      input.removeEventListener('blur', clearFocusStyle);
    };

    input.addEventListener('keydown', clearFocusStyle, { once: true });
    input.addEventListener('blur', clearFocusStyle, { once: true });
  }

  open(triggeredBy) {
    if (triggeredBy) this.activeElement = triggeredBy;

    this.removeAttribute('hidden');
    this.setAttribute('aria-hidden', 'false');
    this.setAttribute('open', '');
    lockScroll();

    if (triggeredBy) triggeredBy.setAttribute('aria-expanded', 'true');

    this.releaseModal?.();
    this.releaseModal = null;

    // Wait until open/active paint so the search field is focusable.
    requestAnimationFrame(() => {
      this.setAttribute('active', '');
      requestAnimationFrame(() => {
        if (!this.hasAttribute('active') || !this.isConnected) return;

        const input = this.getSearchInput();
        const fallback = this.querySelector(
          '[is="close-drawer"]:not([data-fixed-overlay])'
        );

        // Hide the ring before activateDrawer focuses the input.
        this.bindQuietFocus(input);

        this.releaseModal = activateDrawer(this, {
          initialFocus: () => input || fallback,
        });

        // Prefer quiet programmatic focus (no :focus-visible ring).
        if (input) {
          try {
            input.focus({ preventScroll: true, focusVisible: false });
          } catch {
            input.focus({ preventScroll: true });
          }
        }
      });
    });
  }

  close() {
    if (!this.hasAttribute('active')) return;

    this.removeAttribute('active');

    const trigger = this.activeElement;
    trigger?.setAttribute('aria-expanded', 'false');

    this.releaseModal?.(trigger);
    this.releaseModal = null;

    afterDrawerClose(this, () => {
      if (!this.hasAttribute('active')) {
        this.removeAttribute('open');
        this.setAttribute('hidden', '');
        this.setAttribute('aria-hidden', 'true');
        unlockScroll();
      }
    });
  }
}

if (!customElements.get('search-drawer')) {
  customElements.define('search-drawer', SearchDrawer);
}
